'use client';

import React, { useState, useEffect } from 'react';
import { subscribeToNewsletter } from '@/lib/newsletter-action';

export default function PremiumGate({ children, isPremium }: { children: React.ReactNode, isPremium?: boolean }) {
  const [unlocked, setUnlocked] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined' && localStorage.getItem('wpp_premium_unlocked') === 'true') {
      setUnlocked(true);
    }
  }, []);

  if (!isPremium || unlocked) {
    return <>{children}</>;
  }

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setErrorMsg('');

    try {
      // Pass the specific source so it gets saved correctly in Sanity
      const result = await subscribeToNewsletter(email, "Premium Article (Data-Wall)");
      if (result.success || result.message === "ALREADY_SUBSCRIBED") {
        setUnlocked(true);
        if (typeof window !== 'undefined') {
          localStorage.setItem('wpp_premium_unlocked', 'true');
        }
      } else {
        setErrorMsg(result.message);
      }
    } catch (err) {
      setErrorMsg('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {/* Article Content with Max Height and Fade Out */}
      <div className="max-h-[800px] overflow-hidden relative">
        {children}
        {/* Gradient Blur Overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-[400px] bg-gradient-to-t from-background via-background/90 to-transparent z-10 pointer-events-none" />
      </div>

      {/* Soft Data-Wall Gate */}
      <div className="relative z-20 -mt-32 max-w-2xl mx-auto bg-card border border-border shadow-2xl rounded-2xl p-8 sm:p-10 text-center">
        <div className="w-12 h-12 rounded-full bg-brand/10 text-brand flex items-center justify-center mx-auto mb-6">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h3 className="font-serif text-2xl sm:text-3xl font-bold mb-4">Unlock the full Institutional Analysis</h3>
        <p className="text-muted-foreground mb-8 text-sm sm:text-base leading-relaxed">
          This is a premium deep-dive report. Join an elite community of investors, CEOs, and analysts who receive our exclusive global intelligence for free.
        </p>
        
        <form onSubmit={handleUnlock} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input 
            type="email" 
            placeholder="Enter your professional email..."
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 px-4 py-3 bg-background border border-border rounded-md text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-all"
          />
          <button 
            type="submit" 
            disabled={loading}
            className="bg-brand text-white font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-md hover:bg-brand-light transition-colors shadow-lg disabled:opacity-70 disabled:cursor-not-allowed min-w-[140px]"
          >
            {loading ? 'Unlocking...' : 'Continue Reading'}
          </button>
        </form>
        {errorMsg && (
          <p className="text-red-500 text-xs font-bold uppercase tracking-widest mt-3">
            {errorMsg}
          </p>
        )}
        <p className="text-[10px] text-muted-foreground mt-4 uppercase tracking-wider">
          No spam. Unsubscribe anytime.
        </p>
      </div>
    </div>
  );
}
